<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <v-row>
            <v-col cols="3">
              <v-text-field
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.denpyouBangou"
                :label="$t('nyuukaPlan.label.denpyouBangou')"
              />
            </v-col>
            <v-col cols="3">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.nyukaStatus"
                :label="$t('nyuukaPlan.label.nyukaStatus')"
                :items="nyukaStatusItem"
                item-title="label"
                item-value="key"
                clearable
                return-object
              ></v-select>
            </v-col>
            <v-col cols="3">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.nyukamoto"
                :items="nyukamotoItem"
                item-title="code_name"
                item-value="code"
                :label="$t('nyuukaPlan.label.nyukamoto')"
                :clearable="true"
                return-object
              />
            </v-col>
            <v-col cols="3">
              <!-- 入荷先店舗：Adminは全店舗、ゾーンマネージャーは担当店舗から複数選択、店舗ユーザーは自店舗に固定 -->
              <v-select
                data-testid="nyukasaki-select"
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.nyukasakiList"
                :items="nyukasakiItem"
                item-title="code_name"
                item-value="code"
                :label="$t('nyuukaPlan.label.nyukasaki')"
                multiple
                :disabled="nyukasakiFixed"
                :error-messages="nyukasakiError"
              >
                <template v-slot:prepend-item>
                  <v-list-item data-testid="nyukasaki-select-all" :title="$t('nyuukaPlan.label.selectAll')"
                    @click="toggleAllNyukasaki">
                    <template v-slot:prepend>
                      <v-checkbox-btn
                        :model-value="isAllNyukasakiSelected"
                        :indeterminate="isSomeNyukasakiSelected"
                        :ripple="false"
                      />
                    </template>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
                <!-- 複数選択時は1件目のみ表示し、残りは「他n件」にまとめる -->
                <template v-slot:selection="{ item, index }">
                  <div v-if="index === 0" class="nyukasaki-selection" data-testid="nyukasaki-summary">
                    <span v-if="isAllNyukasakiSelected && nyukasakiItem.length > 1">
                      {{ $t('nyuukaPlan.label.nyukasakiAll', [nyukasakiItem.length]) }}
                    </span>
                    <template v-else>
                      <v-chip size="small" label class="nyukasaki-chip">{{ item.title }}</v-chip>
                      <span v-if="searchCondition.nyukasakiList.length > 1" class="text-caption ml-2 text-no-wrap">
                        {{ $t('nyuukaPlan.label.nyukasakiOthers', [searchCondition.nyukasakiList.length - 1]) }}
                      </span>
                    </template>
                  </div>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="3">
              <DatePicker
                ref="nyukaFrom"
                v-model="searchCondition.nyukaDateFrom"
                :label="$t('nyuukaPlan.label.nyukaDateFrom')"
              ></DatePicker>
            </v-col>
            <v-col cols="3">
              <DatePicker
                v-model="searchCondition.nyukaDateTo"
                ref="nyukaTo"
                :label="$t('nyuukaPlan.label.nyukaDateTo')"
              ></DatePicker>
            </v-col>
            <v-col cols="3">
              <v-text-field
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.janCode"
                :label="$t('nyuukaPlan.label.janCode')"
              />
            </v-col>
            <v-col cols="3" class="text-right">
              <v-btn text @click="clear">{{ $t('common.action.clear') }}</v-btn>
              <v-btn
                data-testid="search-btn"
                theme="dark"
                class="ml-4 px-8 bg-grey-darken-3"
                elevation="2"
                @click="search"
                :loading="isSearching"
                >{{ $t('common.action.search') }}</v-btn
              >
            </v-col>
          </v-row>

          <!-- 選択した入荷予定の一括削除（削除は管理者のみ） -->
          <v-row v-if="isAdmin" align="center">
            <v-col cols="4">
              <span data-testid="selected-count">{{ $t('nyuukaPlan.label.selectedCount', [selectedCount]) }}</span>
            </v-col>
            <v-col cols="8" class="text-right">
              <v-btn
                data-testid="clear-selection"
                variant="outlined"
                :disabled="selectedCount == 0"
                @click="clearSelection"
                >{{ $t('nyuukaPlan.action.clearSelection') }}</v-btn
              >
              <v-btn
                data-testid="bulk-delete"
                color="error"
                class="ml-4 px-8"
                elevation="2"
                :disabled="selectedCount == 0"
                @click="confirmBulkDelete"
                >{{ $t('nyuukaPlan.action.bulkDelete') }}</v-btn
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-table density="compact" class="text-no-wrap">
                <thead>
                  <tr>
                    <th v-if="isAdmin" class="np-chk-col">
                      <!-- 表示中のページの未入荷伝票を一括で選択／解除 -->
                      <input
                        type="checkbox"
                        class="np-chk"
                        data-testid="page-check-all"
                        :checked="isPageAllSelected"
                        :indeterminate.prop="isPagePartialSelected"
                        :disabled="pageSelectableKeys.length == 0"
                        @change="togglePageSelection($event.target.checked)"
                      />
                    </th>
                    <th v-for="header in headers" :key="header" @click.stop="tableSort(header)" :style="cursorCheck(header)">
                      {{ $t("nyuukaPlan.label.table." + header) }}</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- 全件表示（数千行）でもチェックの切替で全行を再描画しないよう、変化した行だけ更新する -->
                  <tr v-for="data in filteredDataList" :key="data.no"
                    v-memo="[data, data.no, data.nyukaStatusLabel, selectedKeys.has(data.denpyoNumber)]">
                    <td v-if="isAdmin" class="np-chk-col">
                      <!-- 選択できるのは入荷実績のない（全行が未入荷の）伝票のみ。それ以外は非活性で表示する -->
                      <input
                        type="checkbox"
                        class="np-chk"
                        :data-testid="'row-check-' + data.denpyoNumber"
                        :checked="isSelectable(data) && selectedKeys.has(data.denpyoNumber)"
                        :disabled="!isSelectable(data)"
                        @change="toggleRow(data, $event.target.checked)"
                      />
                    </td>
                    <td v-for="header in headers" :key="header">{{ data[header] }}</td>
                    <td v-if="isAdmin">
                      <v-btn size="small" color="error"
                        :data-testid="'row-delete-' + data.denpyoNumber"
                        :disabled="!isSelectable(data)"
                        @click="confirmDelete(data)">
                        {{ $t('common.action.delete') }}
                      </v-btn>
                    </td>
                  </tr>
                  <tr>
                    <td :colspan="headers.length + (isAdmin ? 2 : 0)">

                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="3"></v-col>
            <v-col cols="6">
              <v-pagination
                v-model="currentPageNo"
                :length="maxPageNo"/>
            </v-col>
            <v-col cols="3">
              <v-row>
                <v-spacer></v-spacer>
                <v-col cols="10" class="d-flex justify-end">
                  <v-select
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    v-model="displayPerPage"
                    :items="displayPerPageItem"
                    item-title="name"
                    item-value="value"
                    :label="$t('common.label.displayCountPerPage')"
                    @update:modelValue = "resetCondition()"
                    return-object/>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" class="text-right">
                  {{displayContentNo.minNo}} - {{displayContentNo.maxNo}}{{ $t('common.label.displayedDataRange') }} / {{ dataList.length }}{{ $t('common.label.totalCountOf') }}
                </v-col>
              </v-row>

            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn
                dark
                class="px-8 bg-indigo-darken-1"
                elevation="2"
                @click.prevent="downloadCsv"
                :disabled="dataList.length == 0"
                >{{ $t('common.action.outputCsv') }}</v-btn
              >
            </v-col>
          </v-row>
        </v-container>
        <ZeroData v-if="zeroDataWarningDialogFlg" :handle-ok="closeZeroDataDialog"/>
        <NyuukaPlanDeleteDialog v-if="deleteDialogVisible" :dialog-data="deleteDialog"
          :handle-ok="confirmDeleteOk" :handle-cancel="confirmDeleteCancel" />
        <NyuukaPlanBulkDeleteDialog v-if="bulkDeleteDialogVisible" :denpyo-number-list="bulkDeleteTargets"
          :handle-done="bulkDeleteDone" :handle-cancel="bulkDeleteCancel" />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import { markRaw } from "vue";
import AppWrapperView from "@/components/views/AppWrapperView";
import DatePicker from "@/components/DatePicker";
import api from "@/apis/staff";
import store from "@/store";
import utils from "@/utils";
import constant from "@/utils/constant";
import ZeroData from "@/components/dialog/ZeroDataDialog";
import NyuukaPlanDeleteDialog from "@/components/dialog/NyuukaPlanDeleteDialog";
import NyuukaPlanBulkDeleteDialog from "@/components/dialog/NyuukaPlanBulkDeleteDialog";

export default {
  components: {AppWrapperView, DatePicker, ZeroData, NyuukaPlanDeleteDialog, NyuukaPlanBulkDeleteDialog},
  props: {},
  computed: {
    constant() {
      return constant;
    },
    // 管理者か（入荷予定の削除は管理者のみ）
    isAdmin() {
      return store.getRole() == constant.ROLE.ADMIN.id;
    },
    //どの部分を表示しているのか
    displayContentNo(){
      const minNo = this.dataList.length != 0 ? 1 + (this.displayPerPage.value * (this.currentPageNo - 1)) : 0;
      const maxNo = this.displayPerPage.value * (this.currentPageNo);
      if(maxNo > this.dataList.length || this.displayPerPage.value == 0){
        return {minNo: minNo, maxNo: this.dataList.length};
      }
      return {minNo: minNo, maxNo: maxNo};
    },

    //最大ページ
    maxPageNo() {
      if(this.displayPerPage.value == 0) return 0;
      return Math.ceil(this.dataList.length/this.displayPerPage.value);
    },

    //ソート後のデータ
    sortedTable() {
      let tempArray = this.dataList.map(x => x);
      if(this.sortState.target != ""){
        //昇順
        if(this.sortState.ascent) tempArray = tempArray.sort((a, b) => {
          if(a[this.sortState.target] > b[this.sortState.target]){
            return 1;
          }
          if(a[this.sortState.target] < b[this.sortState.target]){
            return -1;
          }
          return 0;
        })
        //降順
        else tempArray = tempArray.sort((a, b) => {
          if(a[this.sortState.target] > b[this.sortState.target]){
            return -1;
          }
          if(a[this.sortState.target] < b[this.sortState.target]){
            return 1;
          }
          return 0;
        })
      }

      tempArray = tempArray.map((x, index) => {
        x.no = index + 1;
        // 言語切替に追従させるため、表示時にラベルを付与する
        x.nyukaStatusLabel = this.$t("nyuukaPlan.label.status." + x.nyukaStatus);
        return x;
      })

      return tempArray;
    },
    filteredDataList() {
      const tempArray = this.sortedTable;
      return tempArray.slice(this.displayContentNo.minNo-1, this.displayContentNo.maxNo);
    },
    // 入荷ステータス（未指定の場合は全ステータス）
    nyukaStatusItem() {
      return constant.RECEIVE_STATUS_LIST.map(key => ({key: key, label: this.$t("nyuukaPlan.label.status." + key)}));
    },
    // ヘッダー
    headers() {
      return this.$getTableDef("NyuukaPlanPage");
    },

    // 入荷先店舗：全選択状態（「すべて選択」のチェック状態）
    isAllNyukasakiSelected() {
      return this.nyukasakiItem.length > 0 && this.searchCondition.nyukasakiList.length == this.nyukasakiItem.length;
    },
    // 入荷先店舗：一部選択状態（「すべて選択」の中間状態）
    isSomeNyukasakiSelected() {
      return this.searchCondition.nyukasakiList.length > 0 && !this.isAllNyukasakiSelected;
    },

    // 選択中の伝票数
    selectedCount() {
      return this.selectedKeys.size;
    },
    // 表示中の行（5/10/15件表示は現在のページ、全件表示はリスト全体）で選択可能（入荷実績のない）な伝票番号（重複排除）
    pageSelectableKeys() {
      const keys = new Set();
      for (const row of this.filteredDataList) {
        if (this.isSelectable(row)) keys.add(row.denpyoNumber);
      }
      return Array.from(keys);
    },
    // 表示中のページの選択可能な伝票がすべて選択されている
    isPageAllSelected() {
      const keys = this.pageSelectableKeys;
      return keys.length > 0 && keys.every(k => this.selectedKeys.has(k));
    },
    // 表示中のページの選択可能な伝票が一部だけ選択されている
    isPagePartialSelected() {
      return !this.isPageAllSelected && this.pageSelectableKeys.some(k => this.selectedKeys.has(k));
    },
  },
  watch: {
    // 入荷先店舗を選択し直したら、未選択エラーは消す
    'searchCondition.nyukasakiList'(list) {
      if (list && list.length > 0) this.nyukasakiError = "";
    },
  },
  data: () => ({
    currentPageNo: 1,
    //１ページ表示数
    displayPerPage: {},
    displayPerPageItem: [],
    //ソートの状態
    sortState: {target: "", ascent: true},
    nyukamotoItem: [],
    nyukasakiItem: [],
    // 入荷先店舗を自店舗に固定する（店舗ユーザー）
    nyukasakiFixed: false,
    // 入荷先店舗の未選択エラー（Admin／ゾーンマネージャーが全解除して検索した場合）
    nyukasakiError: "",

    dataList: [],
    searchCondition: {
      denpyouBangou: "",    //伝票番号
      nyukaStatus: null,    //入荷ステータス
      nyukamoto: null,      //入荷元
      nyukasakiList: [],    //入荷先店舗コード（複数選択）
      nyukaDateFrom: "",    //入荷予定日from
      nyukaDateTo: "",      //入荷予定日to
      janCode: "",          //JANコード
    },

    // 0件取得時の警告ダイアログ
    zeroDataWarningDialogFlg: false,
    // 検索中
    isSearching: false,

    // 削除ダイアログ表示フラグ
    deleteDialogVisible: false,
    // 削除ダイアログに渡すデータ
    deleteDialog: {
      plan: null,
    },

    // 選択中の伝票番号。数千件の選択でも軽く動くよう、Setを丸ごと差し替える（要素単位のリアクティブ追跡はしない）
    selectedKeys: markRaw(new Set()),
    // 一括削除ダイアログ表示フラグ
    bulkDeleteDialogVisible: false,
    // 一括削除ダイアログに渡す伝票番号リスト
    bulkDeleteTargets: [],

  }),
  methods: {

    //カーソルの変更
    cursorCheck(){
      return "cursor: pointer;"
    },

    //店舗検索リスト取得
    // selectSiteFlag：0：他店舗含む / 1：自店舗のみ
    getValidShopSearchList(){
      //入荷元店舗リスト取得
      api.post("/store/search/minimal", {selectSiteFlag: 0})
      .then((response)=>{
        if(response.status == 1) return; //do nothing
        response.data.results?.shopList.forEach(element => {
          this.nyukamotoItem.push({name: element.name, code: element.code, code_name: element.code+'_'+element.name})
        })
      })
      .catch(() => {
        //do nothing
      })
      //入荷先店舗リスト取得
      // Admin：全店舗から複数選択 / ゾーンマネージャー：担当店舗から複数選択（いずれも初期値は全選択）
      // 店舗ユーザー：自店舗のみ（全選択で固定）
      const role = store.getRole();
      const isAdmin = role == constant.ROLE.ADMIN.id;
      this.nyukasakiFixed = !isAdmin && role != constant.ROLE.ZONE_MANAGER.id;
      api.post("/store/search/minimal", {selectSiteFlag: isAdmin ? 0 : 1})
      .then((response)=>{
        if(response.status == 1) return; //do nothing
        response.data.results?.shopList.forEach(element => {
          this.nyukasakiItem.push({name: element.name, code: element.code, code_name: element.code+'_'+element.name})
        })
        this.searchCondition.nyukasakiList = this.defaultNyukasakiList();
      })
      .catch(() => {
        //do nothing
      })
    },

    //テーブルの条件リセット
    resetCondition(){
      //１ページ目
      this.currentPageNo = 1;
      //ソートなし
      this.sortState.target = "";
      this.sortState.ascent = true;
    },
    tableSort(headValue) {
      if(headValue == "no") return;
      this.currentPageNo = 1;
      if(this.sortState.target == headValue){
        this.sortState.ascent = !this.sortState.ascent;
      }
      else{
        this.sortState.target = headValue;
        this.sortState.ascent = true;
      }
    },

    clear() {
      this.$refs.nyukaTo.clear()
      this.$refs.nyukaFrom.clear()
      this.searchCondition = {
        denpyouBangou: "",    //伝票番号
        nyukaStatus: this.defaultNyukaStatus(), //入荷ステータス
        nyukamoto: null,      //入荷元店舗 name:codeを中に持っている
        nyukasakiList: this.defaultNyukasakiList(), //入荷先店舗コード（初期値は全選択）
        janCode: "",          //JANコード
      }
      this.clearSelection();
    },

    // 入荷先店舗の初期値：全店舗を選択
    defaultNyukasakiList() {
      return this.nyukasakiItem.map(e => e.code);
    },

    // 入荷先店舗「すべて選択」：全選択済みなら全解除、それ以外は全選択
    toggleAllNyukasaki() {
      this.searchCondition.nyukasakiList = this.isAllNyukasakiSelected ? [] : this.defaultNyukasakiList();
    },

    // 初期表示の入荷ステータス：未入荷
    defaultNyukaStatus() {
      return this.nyukaStatusItem.find(e => e.key == constant.RECEIVE_STATUS.NOT_IN_STOCK);
    },

    closeZeroDataDialog() {
      this.zeroDataWarningDialogFlg = false;
    },

    search() {
      // Admin／ゾーンマネージャーが入荷先店舗をすべて解除した場合は検索しない
      // （全選択と同じ空配列を送ると全店舗が対象になってしまうため）
      if (!this.nyukasakiFixed && (this.searchCondition.nyukasakiList ?? []).length == 0) {
        this.nyukasakiError = this.$t('nyuukaPlan.message.nyukasakiRequired');
        return;
      }
      this.nyukasakiError = "";
      this.isSearching = true;
      // 検索し直すと、前回の選択は破棄する
      this.clearSelection();
      api.post("/nyuka/plan/search", {
          denpyoNumber: this.searchCondition.denpyouBangou,
          nyukaStatus: this.searchCondition.nyukaStatus?.key ?? "",
          nyukamotoCode: this.searchCondition.nyukamoto?.code ?? "",
          // 全選択・未選択（空配列）の場合は入荷先店舗で絞り込まない（店舗マスタ未登録の入荷先も対象にするため）
          // Admin以外はAPI側で自店舗（ゾーンマネージャーは担当店舗）に限定される
          nyukasakiCodeList: this.isAllNyukasakiSelected ? [] : (this.searchCondition.nyukasakiList ?? []),
          nyukaDateFrom: this.searchCondition.nyukaDateFrom?.replaceAll("-","") ?? "",
          nyukaDateTo: this.searchCondition.nyukaDateTo?.replaceAll("-","") ?? "",
          janCode: this.searchCondition.janCode,
        }
      )
      .then((response)=>{
        // 初期表示順：入荷予定日 → 伝票番号 → 伝票番号内行番号
        const planList = (response.data.results?.nyukaList ?? []).slice().sort((a, b) =>
          (a.nyukaDate ?? "").localeCompare(b.nyukaDate ?? "")
          || (a.denpyoNumber ?? "").localeCompare(b.denpyoNumber ?? "")
          || Number(a.lineNumber) - Number(b.lineNumber));
        this.dataList = planList.map(x => ({
          ...x,
          nyukaDate: utils.putSplitWordToCalendar(x.nyukaDate),
        }));
        this.resetCondition();

        // 0件ダイアログ処理
        if(this.dataList.length == 0) this.zeroDataWarningDialogFlg = true;
        this.isSearching = false;

      })
      .catch(() => {
        // 0件 dialog
        this.dataList = [];
        this.zeroDataWarningDialogFlg = true;
        this.isSearching = false;
      })
    },
    downloadCsv() {
      // 品名にカンマを含む可能性があるため、ダブルクォートで囲んで出力する
      utils.quotedSimpleDownloadCsv(this.headers, this.sortedTable, "nyuukaPlan.label.table.", 'nyuukaPlan.fileNamePrefix.csv', this.$t);
    },

    // 未入荷の行か
    isUnreceived(row) {
      return row.nyukaStatus == constant.RECEIVE_STATUS.NOT_IN_STOCK;
    },

    // 選択・削除できる伝票か。行自体が未入荷であり、かつ同一伝票内に入荷実績のある行がないこと。
    // （入荷ステータスで絞り込んだ場合、入荷済みの行は検索結果に含まれないため、APIが返す伝票単位のフラグで判定する）
    isSelectable(row) {
      return this.isUnreceived(row) && !row.denpyoHasResult;
    },

    // 選択を差し替える（変更通知は1回だけ）
    setSelection(keys) {
      this.selectedKeys = markRaw(keys);
    },

    // 選択解除
    clearSelection() {
      this.setSelection(new Set());
    },

    // 1行の選択／解除
    toggleRow(row, checked) {
      const next = new Set(this.selectedKeys);
      if (checked) next.add(row.denpyoNumber);
      else next.delete(row.denpyoNumber);
      this.setSelection(next);
    },

    // 表示中のページの未入荷伝票を選択／解除
    togglePageSelection(checked) {
      const next = new Set(this.selectedKeys);
      for (const key of this.pageSelectableKeys) {
        if (checked) next.add(key);
        else next.delete(key);
      }
      this.setSelection(next);
    },

    // 一括削除ダイアログ表示
    confirmBulkDelete() {
      if (this.selectedCount == 0) return;
      this.bulkDeleteTargets = Array.from(this.selectedKeys);
      this.bulkDeleteDialogVisible = true;
    },

    // 一括削除ダイアログ非表示
    closeBulkDeleteDialog() {
      this.bulkDeleteDialogVisible = false;
      this.bulkDeleteTargets = [];
    },

    // 一括削除ダイアログキャンセル（選択は維持する）
    bulkDeleteCancel() {
      this.closeBulkDeleteDialog();
    },

    // 一括削除の結果ダイアログを閉じた：現在の条件で再検索（選択はsearch内で解除）
    bulkDeleteDone() {
      this.closeBulkDeleteDialog();
      this.search();
    },

    // 削除ダイアログ表示
    confirmDelete(item) {
      this.deleteDialogVisible = true;
      this.deleteDialog.plan = item;
    },

    // 削除ダイアログ非表示
    closeDeleteDialog() {
      this.deleteDialogVisible = false;
      this.deleteDialog.plan = null;
    },

    // 削除ダイアログキャンセル
    confirmDeleteCancel() {
      this.closeDeleteDialog();
    },

    // 削除ダイアログOK押下（削除成功時）
    confirmDeleteOk() {
      this.closeDeleteDialog();
      this.search();
    },
  },
  created() {
    this.getValidShopSearchList()
  },
  mounted() {
    this.displayPerPage = utils.getDisplayPerPage(this.$t)
    this.displayPerPageItem = utils.getDisplayPerPageItem(this.$t);
    this.searchCondition.nyukaStatus = this.defaultNyukaStatus();
  }
};
</script>

<style>
/* 選択チェックボックス（件数が多くても軽いよう標準のinputを使用） */
.np-chk {
  width: 18px;
  height: 18px;
  cursor: pointer;
  vertical-align: middle;
}
.np-chk:disabled {
  cursor: not-allowed;
}
.np-chk-col {
  width: 48px;
  text-align: center;
}
/* 入荷先店舗の選択表示（1件目＋「他n件」）：枠内に収める */
.nyukasaki-selection {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}
.nyukasaki-chip.v-chip.v-chip--size-small {
  min-width: 0;
  margin: 0;
  height: 22px;
}
.nyukasaki-chip .v-chip__content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
